import { initAxios } from '../../../utils/axiosHelper';
import {
  BulkUploadResponse,
  CreateTariffResponse,
  ITariff,
  PaginatedResponse,
  PaginationParams,
} from './tariffs';

// All tariff CRUD/bulk/status endpoints now live on the standalone analyzer
// microservice, not bite-2.0's own API. `getProductTariffHistory` is the only
// exception -- it reads bite-db and was never migrated, so it deliberately uses
// a bare initAxios() against REACT_APP_API_URL.
//
// QA/prod set REACT_APP_TARIFF_API_URL to an absolute host. Locally it's unset,
// so we fall back to the nginx path proxy at `/analyzer-service` (see
// nginx/nginx.conf) -- same-origin, which keeps the initAxios auth interceptor
// working and avoids a CORS preflight. Same shape as ENTITY_SERVICE_BASE_URL.
// The fallback MUST be a non-empty string: `|| ''` is falsy, so initAxios would
// silently fall through to REACT_APP_API_URL and hit bite-server, which no
// longer serves any of these routes.

const tariffApi = () =>
  initAxios(process.env.REACT_APP_TARIFF_API_URL || '/analyzer-service');

// Get tariffs by database ID
export const getTariffsByDatabaseId = async (
  id?: string,
  params?: Partial<PaginationParams>,
): Promise<PaginatedResponse<ITariff>> => {
  try {
    if (!id) {
      return {
        data: [],
        pagination: {
          currentPage: 1,
          totalPages: 0,
          totalItems: 0,
          itemsPerPage: 10,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    }

    const instance = await tariffApi();
    if (!instance) {
      return {
        data: [],
        pagination: {
          currentPage: 1,
          totalPages: 0,
          totalItems: 0,
          itemsPerPage: 10,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    }

    const queryParams = new URLSearchParams({
      page: (params?.page || 1).toString(),
      limit: (params?.limit || 10).toString(),
      sortBy: params?.sortBy || 'hit_count',
      sortOrder: params?.sortOrder || 'desc',
      search: params?.search || '',
      tab: params?.tab || 'All',
    });

    // Risk-colour tab. Only sent when a colour is actually selected so the
    // "All" tab's query string stays byte-identical to what it was before.
    if (params?.statusColor) {
      queryParams.set('statusColor', params.statusColor);
    }

    const resp = await instance.get(
      `/tariffs/database/${id}?${queryParams.toString()}`,
    );

    return {
      data: resp.data?.data || [],
      totals: resp.data?.totals,
      pagination: resp.data?.pagination || {
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: 10,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  } catch (err) {
    console.error('Error fetching tariffs:', err);
    return {
      data: [],
      pagination: {
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: 10,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  }
};

// Get all tariffs by database ID (for export)
export const getAllTariffsByDatabaseId = async (
  id?: string,
): Promise<ITariff[]> => {
  try {
    if (!id) {
      return [];
    }

    const instance = await tariffApi();
    if (!instance) {
      return [];
    }

    const resp = await instance.get(`/tariffs/database/${id}/all`);

    return resp.data?.data || [];
  } catch (err) {
    console.error('Error fetching all tariffs:', err);
    return [];
  }
};

export type TariffExportFormat =
  | 'BITE_DEFAULT'
  | 'TARIFF_HORIZONTAL'
  | 'CARGOWISE';

export interface TariffCsvDownloadResult {
  total: number;
  truncated: boolean;
}

/**
 * Downloads the tariff export as CSV built by the analyzer service.
 *
 * The CSV used to be assembled here: fetch every row as JSON, parse it, expand
 * it into one array entry per (line x duty row), join. That put the whole
 * database in the tab's heap twice over and capped the export well below the
 * row counts users have. The service now streams text/csv off a cursor, so the
 * browser only ever holds opaque bytes.
 *
 * responseType 'blob' is the point -- axios must not try to parse this.
 * `tz` goes along because the old builder formatted dates in browser-local
 * time and the container runs UTC; without it every date could shift a day.
 */
export const downloadTariffCsv = async (
  id: string,
  exportFormat: TariffExportFormat,
  fileName: string,
): Promise<TariffCsvDownloadResult> => {
  const instance = await tariffApi();
  if (!instance) {
    throw new Error('Tariff service is unavailable');
  }

  let finalFileName = fileName.trim();
  if (!finalFileName.toLowerCase().endsWith('.csv')) {
    finalFileName += '.csv';
  }

  const queryParams = new URLSearchParams({
    format: exportFormat,
    fileName: finalFileName,
    tz: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  });

  const resp = await instance.get(
    `/tariffs/database/${id}/export.csv?${queryParams.toString()}`,
    { responseType: 'blob' },
  );

  const url = URL.createObjectURL(
    new Blob([resp.data], { type: 'text/csv;charset=utf-8;' }),
  );
  try {
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', finalFileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    // The old inline version never revoked this, leaking the blob for the
    // lifetime of the document -- which matters a lot more now that a blob
    // can be hundreds of megabytes.
    URL.revokeObjectURL(url);
  }

  return {
    total: Number(resp.headers?.['x-tariff-export-total'] ?? 0),
    truncated: String(resp.headers?.['x-tariff-export-truncated']) === 'true',
  };
};

const BATCH_LIMIT = 200;

export const getTariffDatabaseSummariesBatch = async (
  databaseIds: string[],
): Promise<
  Record<
    string,
    { total_not_reviewed: number; total_value: number; total_duty: number }
  >
> => {
  if (databaseIds.length === 0) return {};
  try {
    const instance = await tariffApi();
    if (!instance) return {};
    const chunks: string[][] = [];
    for (let i = 0; i < databaseIds.length; i += BATCH_LIMIT) {
      chunks.push(databaseIds.slice(i, i + BATCH_LIMIT));
    }
    const results = await Promise.all(
      chunks.map((chunk) =>
        instance
          .get(`/tariffs/databases/batch?ids=${chunk.join(',')}`)
          .then((r) => r.data?.data ?? {})
          .catch(() => ({})),
      ),
    );
    return Object.assign({}, ...results);
  } catch (err) {
    console.error('Error fetching batch tariff summaries:', err);
    return {};
  }
};

export const getTariffsByGroupId = async (
  id?: string,
  params?: Partial<PaginationParams>,
): Promise<PaginatedResponse<ITariff>> => {
  try {
    if (!id) {
      return {
        data: [],
        pagination: {
          currentPage: 1,
          totalPages: 0,
          totalItems: 0,
          itemsPerPage: 10,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    }

    const instance = await tariffApi();
    if (!instance) {
      return {
        data: [],
        pagination: {
          currentPage: 1,
          totalPages: 0,
          totalItems: 0,
          itemsPerPage: 10,
          hasNextPage: false,
          hasPreviousPage: false,
        },
      };
    }

    const queryParams = new URLSearchParams({
      page: (params?.page || 1).toString(),
      limit: (params?.limit || 10).toString(),
      sortBy: params?.sortBy || 'hit_count',
      sortOrder: params?.sortOrder || 'desc',
      search: params?.search || '',
      databaseName: params?.databaseName || '',
    });

    if (params?.exclusions && params.exclusions.length > 0) {
      params.exclusions.forEach((exclusion: any) => {
        queryParams.append('exclusions', exclusion);
      });
    }

    const resp = await instance.get(
      `/tariffs/group/${id}?${queryParams.toString()}`,
    );

    return {
      data: resp.data?.data || [],
      pagination: resp.data?.pagination || {
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: 10,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  } catch (err) {
    console.error('Error fetching tariffs:', err);
    return {
      data: [],
      pagination: {
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: 10,
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
  }
};

// Get tariff by ID
export const getTariffById = async (id?: string): Promise<ITariff | null> => {
  try {
    if (!id) {
      throw Error('No ID provided');
    }
    const instance = await tariffApi();
    if (!instance) {
      throw Error('Failed to initialize request');
    }

    const resp = await instance.get(`/tariff/${id}`);
    const tariffData = resp.data?.data || resp.data?.result;
    if (tariffData) {
      return {
        ...tariffData,
        type: 'tariff',
      };
    }
    return null;
  } catch (err) {
    throw err;
  }
};

// Update tariff
export const updateTariff = async (
  id: string,
  tariffData: Partial<any>,
): Promise<{ success: boolean; data?: any; message?: string }> => {
  try {
    const instance = await tariffApi();
    if (!instance) {
      return { success: false, message: 'Failed to initialize API client' };
    }

    const resp = await instance.put(
      `/tariff/${id}`,
      JSON.stringify(tariffData),
    );
    return { success: true, data: resp.data?.data };
  } catch (err: any) {
    return {
      success: false,
      message: err.response?.data?.message || 'Failed to update tariff',
    };
  }
};

// Update tariff status
export const updateTariffStatus = async (
  id: string,
  statusData: { status_flag: string },
): Promise<boolean> => {
  try {
    const instance = await tariffApi();
    if (!instance) {
      return false;
    }

    const resp = await instance.put(
      `/tariff-status/${id}`,
      JSON.stringify(statusData),
    );
    return resp.data?.success || false;
  } catch (err) {
    return false;
  }
};

// Delete tariff
export const deleteTariff = async (id: string): Promise<boolean> => {
  try {
    const instance = await tariffApi();
    if (!instance) {
      return false;
    }

    const resp = await instance.delete(`/tariff/${id}`);
    return resp.data?.success || false;
  } catch (err) {
    return false;
  }
};

// Bulk delete tariffs
export const deleteBulkTariffs = async (
  tariffIds: string[],
): Promise<{
  successful_deletes: number;
  failed_deletes: number;
  total_requested: number;
}> => {
  try {
    const instance = await tariffApi();
    if (!instance) {
      throw new Error('Failed to initialize API client');
    }

    const resp = await instance.delete('/tariffs-bulk', {
      data: { tariff_ids: tariffIds },
    });

    return (
      resp.data?.data || {
        successful_deletes: 0,
        failed_deletes: tariffIds.length,
        total_requested: tariffIds.length,
      }
    );
  } catch (err: any) {
    throw new Error(err.response?.data?.message || 'Failed to delete tariffs');
  }
};

// Create/Add a single tariff
export const createTariff = async (
  tariffData: any,
  teamId?: string,
): Promise<CreateTariffResponse> => {
  try {
    const instance = await tariffApi();
    if (!instance) {
      return {
        success: false,
        data: null,
      };
    }

    // FIX: Changed from '/add-supplier' to '/tariff'
    const resp = await instance.post<CreateTariffResponse>(
      '/tariff',
      JSON.stringify(tariffData),
      {
        headers: {
          'x-group-id': teamId || '',
        },
      },
    );
    return resp.data;
  } catch (err) {
    throw err;
  }
};

// Bulk upload tariffs
export const bulkUploadTariffs = async (
  formData: FormData,
  // teamId is unused: the analyzer service scopes the upload via database_id
  // (already in formData) and doesn't read an x-group-id header. Sending one
  // trips its CORS allow-list (Origin/X-Requested-With/Content-Type/Accept/
  // Authorization only) and blocks the request at the preflight.
  _teamId?: string,
): Promise<BulkUploadResponse> => {
  try {
    const instance = await tariffApi();
    if (!instance) {
      return {
        success: false,
        data: [],
      };
    }

    // Remove Content-Type header to let the browser set it with the
    // multipart boundary (see database.service.ts's bulkUploadSuppliers).
    delete instance.defaults.headers.common['Content-Type'];
    delete instance.defaults.headers.post['Content-Type'];
    delete instance.defaults.headers['Content-Type'];

    const resp = await instance.post<BulkUploadResponse>(
      '/tariffs-bulk-upload',
      formData,
    );
    return resp.data;
  } catch (err) {
    throw err;
  }
};

// Standard Bulk Create (JSON) - Added this as it matches your router's '/tariffs-bulk'
export const createBulkTariffs = async (
  tariffs: Partial<ITariff>[],
  databaseId: string,
  createdBy?: string,
  teamId?: string,
): Promise<BulkUploadResponse> => {
  try {
    const instance = await tariffApi();
    if (!instance) {
      return { success: false, data: [] };
    }

    const payload = {
      queries: tariffs,
      database_id: databaseId,
      created_by: createdBy,
    };

    const resp = await instance.post('/tariffs-bulk', payload, {
      headers: { 'x-group-id': teamId || '' },
    });
    return resp.data;
  } catch (err) {
    throw err;
  }
};

// Bulk create tariffs without duplicate check
export const createBulkTariffsWithoutDuplicateCheck = async (
  tariffs: Partial<ITariff>[],
  databaseId: string,
  createdBy?: string,
  teamId?: string,
  uploadStartTime?: string,
): Promise<{
  success: boolean;
  data: Array<{
    id: string;
    name: string;
    database_id?: string;
    database_tariff_id?: string;
  }>;
}> => {
  try {
    const instance = await tariffApi();
    if (!instance) {
      return {
        success: false,
        data: [],
      };
    }

    const payload = {
      queries: tariffs,
      database_id: databaseId,
      created_by: createdBy,
      uploadStartTime: uploadStartTime,
    };

    const resp = await instance.post(
      '/tariffs-bulk-no-duplicate-check',
      payload,
      {
        headers: {
          'x-group-id': teamId || '',
        },
      },
    );
    return resp.data;
  } catch (err) {
    throw err;
  }
};

// Client-side filtering function for tariffs
export const filterTariffs = (
  tariffs: ITariff[],
  searchTerm: string,
): ITariff[] => {
  if (!searchTerm || searchTerm.trim() === '') {
    return tariffs;
  }

  const lowerSearchTerm = searchTerm.toLowerCase().trim();

  return tariffs.filter((tariff) => {
    const hts_code = tariff.hts_code?.toLowerCase() || '';
    const description = tariff.description?.toLowerCase() || '';
    const country_of_origin = tariff.country_of_origin?.toLowerCase() || '';
    const product_code = tariff.product_code?.toString().toLowerCase() || '';

    return (
      hts_code.includes(lowerSearchTerm) ||
      description.includes(lowerSearchTerm) ||
      country_of_origin.includes(lowerSearchTerm) ||
      product_code.includes(lowerSearchTerm)
    );
  });
};

// Update status of all tariffs in a database
export const updateAllTariffsStatus = async (
  databaseId: string,
  status_flag: string,
): Promise<{ success: boolean; data?: any; message?: string }> => {
  try {
    const instance = await tariffApi();
    if (!instance) {
      return { success: false, message: 'Client Init Failed' };
    }

    const resp = await instance.put(
      `/tariffs/database/${databaseId}/status`,
      JSON.stringify({ status_flag: status_flag }),
    );
    return resp.data;
  } catch (err: any) {
    return { success: false, message: err.message };
  }
};

// Transition tariff status (From -> To)
export const updateTariffsStatusFromTo = async (
  databaseId: string,
  scope: string,
  toStatus: string,
  fromStatus?: string,
  tariffIds?: string[],
): Promise<{ success: boolean; message: string; count: number }> => {
  try {
    const instance = await tariffApi();
    if (!instance) {
      throw new Error('Failed to initialize API client');
    }

    const resp = await instance.put(
      `/tariffs/database/${databaseId}/status/transition`,
      {
        scope: scope,
        to_status: toStatus,
        from_status: fromStatus,
        tariff_ids: tariffIds,
      },
    );
    return resp.data;
  } catch (err: any) {
    throw new Error(
      err.response?.data?.message || 'Failed to update entities status',
    );
  }
};

// Toggle doc visibility
export const toggleTariffDocVisibility = async (
  itemId: string,
  parentId: string,
): Promise<boolean> => {
  try {
    const instance = await tariffApi();
    if (!instance) {
      return false;
    }

    const resp = await instance.post('/toggle-tariff-doc-visibility', {
      item_id: itemId,
      parent_id: parentId,
    });
    return resp.data?.success || false;
  } catch (err) {
    return false;
  }
};

export const reRunTariffDatabase = async (
  databaseId: string,
): Promise<{ success: boolean; message: string }> => {
  const instance = await tariffApi();
  if (!instance) throw new Error('Failed to initialize API client');
  const resp = await instance.post(`/database/${databaseId}/tariff-rerun`);
  return resp.data;
};

export const saveTariffResult = async (
  databaseTariffId: string,
  resultData: any,
): Promise<{ success: boolean; data: any }> => {
  const instance = await tariffApi();

  if (!instance) {
    throw new Error('Failed to initialize API client');
  }
  const response = await instance.post(
    `/tariff-results/${databaseTariffId}`,
    resultData,
  );
  return response.data;
};

// One before→after field change in a tariff diff.
export interface ITariffFieldChange<T> {
  from: T | null;
  to: T | null;
  changed: boolean;
}

export interface ITariffProgram {
  code?: string;
  _codeDigits?: string;
  type?: string;
  rate?: string;
}

export interface ITariffDiff {
  changed: boolean;
  is_base_snapshot: boolean;
  from_screen_date: string | null;
  to_screen_date: string | null;
  status_message: ITariffFieldChange<string>;
  status_color: ITariffFieldChange<string>;
  base_duty_rate: ITariffFieldChange<string>;
  duty_cost: ITariffFieldChange<number>;
  total_cost: ITariffFieldChange<number>;
  additional_duty: ITariffFieldChange<number>;
  programs_added: ITariffProgram[];
  programs_removed: ITariffProgram[];
  programs_changed: Array<{
    code: string;
    type: string;
    from_rate: string;
    to_rate: string;
  }>;
}

// One snapshot in a product's tariff history (a change-point).
export interface IProductTariffHistoryEntry {
  id: string;
  screening_result_id: string | null;
  database_product_id: string;
  is_base_snapshot: boolean;
  screen_date: string | null;
  results_hash: string | null;
  tariff_diff: ITariffDiff | null;
  saved_at: string | null;
  created_at: string;
  summary: {
    status_message: string | null;
    status_color: string | null;
    base_duty_rate: string | null;
    total_cost: number | null;
    duty_cost: number | null;
    additional_duty: number | null;
    programs: ITariffProgram[];
  };
}

// Full tariff snapshot history for a product's membership in a database
// (database_product_id), newest first. Powers the tariff-history modal.
export const getProductTariffHistory = async (
  databaseProductId?: string,
): Promise<IProductTariffHistoryEntry[]> => {
  try {
    if (!databaseProductId) {
      return [];
    }
    const instance = await initAxios();
    if (!instance) {
      return [];
    }
    const resp = await instance.get(
      `/bite-db/product-tariff-history/${databaseProductId}`,
    );
    return resp.data?.data?.history || [];
  } catch (err) {
    console.error('Error fetching product tariff history:', err);
    return [];
  }
};

// ---------------------------------------------------------------------------
// Duty calculation
// ---------------------------------------------------------------------------
// The engine that prices a line lives in biteservices (services/shared/src/
// utils/tariffCalculation.util.ts) and is the same code the ACE Analyzer runs.
// bite-2.0's client currently carries five private copies of it, all older, so
// the Tariff Calculator, Classify and the two Product DB views can disagree
// with ACE on the same line. This endpoint is how they stop.
//
// Only the inputs go over the wire. The service resolves the tariff rule set
// and the HTS record in force on the entry date itself — shipping every tariff
// rule to the browser so it can run a private engine is exactly what produced
// the five copies.

/** Mirrors the engine's TariffCalculationInput['inputs']. */
export interface TariffCalculationInputs {
  htsCode: string;
  originCountry: string;
  productValue: number;
  transportMethod?: string;
  entryDate?: string;
  loadingDate?: string;
  materialPct?: number;
  materialPctIsPercent?: boolean;
  materialPctByKey?: Record<string, number>;
  useFTA?: boolean | null;
  spiCode?: string;
  countryOfSmeltPour?: string;
  amountValue?: number;
  amountUnit?: string;
  amountInputs?: Record<string, number>;
  amountsByUnit?: Record<string, number>;
  defaultSpecificQuantity?: number;
  claimedCh99Codes?: string[];
  [key: string]: unknown;
}

/**
 * Price one line server-side.
 *
 * Throws on a non-2xx so a caller can fall back to its local engine rather
 * than render a blank result — while both paths exist, a failure here must not
 * be worse than not having called it.
 */
export const calculateTariffDuty = async (
  inputs: TariffCalculationInputs,
): Promise<any> => {
  const instance = await tariffApi();
  const resp = await instance?.post('/tariff/calculate', inputs);
  if (!resp || resp.status !== 200) {
    throw new Error(
      `tariff calculate failed (${resp ? resp.status : 'no response'})`,
    );
  }
  return resp.data;
};
